import { inject } from '@angular/core';
import { signalStore, withState, withMethods, patchState, WritableStateSource, withHooks } from '@ngrx/signals';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { firstValueFrom } from 'rxjs';
import { withSlowLoadingTimer, watchStoreErrors, extractErrorMessage } from '../core/utils';
import { MessageService } from '../services';

export interface UserPlanState {
  plan: 'FREE' | 'PRO' | null;
  // Entitlements and usage are set together (either from API or fallback defaults)
  // They are null only before the first load attempt
  entitlements: {
    maxFarms: number;
    maxFieldsPerFarm: number;
    maxPlantingsPerFarm: number;
  } | null;
  usage: {
    farms: number;
    fields: number;
    plantings: number;
  } | null;
  loading: boolean;
  slowLoading: boolean;
  error: string | null;
}

const initialState: UserPlanState = {
  plan: null,
  entitlements: null,
  usage: null,
  loading: false,
  slowLoading: false,
  error: null,
};

interface EntitlementsResponse {
  plan: string;
  entitlements: {
    maxFarms: number;
    maxFieldsPerFarm: number;
    maxPlantingsPerFarm: number;
  };
  usage: {
    farms: number;
    fields: number;
    plantings: number;
  };
}

export const UserPlanStore = signalStore(
  { providedIn: 'root' },

  withState(initialState),

  withMethods((store, http = inject(HttpClient)) => {
    const apiUrl = `${environment.apiUrl}/planner/entitlements`;

    const writable = store as unknown as WritableStateSource<UserPlanState>;

    return {
      loadPlan: async () => {
        await withSlowLoadingTimer(store, writable, async () => {
          try {
            const response = await firstValueFrom(http.get<EntitlementsResponse>(apiUrl));
            
            // Normalize plan to uppercase 'FREE' or 'PRO'
            const plan = response.plan?.toUpperCase() === 'PRO' ? 'PRO' : 'FREE';
            
            patchState(writable, {
              plan,
              entitlements: response.entitlements,
              usage: response.usage,
              error: null,
            });
          } catch (err: unknown) {
            const errorMessage = extractErrorMessage(err, 'Failed to load user plan', {
              notFoundMessage: 'Entitlements API not found (404) — check backend or environment.apiUrl',
            });
            
            // On error, fall back to FREE plan with default limits
            // This allows the app to continue functioning
            patchState(writable, {
              plan: 'FREE',
              entitlements: {
                maxFarms: 1,
                maxFieldsPerFarm: 20,
                maxPlantingsPerFarm: 200,
              },
              usage: {
                farms: 0,
                fields: 0,
                plantings: 0,
              },
              error: errorMessage,
            });
          }
        });
      },
      resetStore: () => {
        patchState(writable, initialState);
      },
    };
  }),

  withHooks({
    onInit(store) {
      const messageService = inject(MessageService);
      // Automatically show error toasts when errors occur
      watchStoreErrors(store.error, messageService, 'User Plan Error');
    },
  })
);
